import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SlidePlacement } from "@/components/SlidePlacement";
import { FilterBar } from "@/components/FilterBar";

export default function Shop({ mode }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const search = params.get("search");
  const [data, setData] = useState({ products: [], total: 0 });
  const [cat, setCat] = useState(null);
  const [sort, setSort] = useState("created");
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [inStock, setInStock] = useState(false);

  useEffect(() => { api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {}); }, []);

  const resetFilters = () => { setSelectedBrands([]); setPriceMin(""); setPriceMax(""); setInStock(false); };
  useEffect(() => { resetFilters(); }, [slug, mode, search]);

  useEffect(() => {
    setLoading(true);
    const run = () => {
      const q = new URLSearchParams();
      q.set("sort", sort);
      q.set("limit", "60");
      if (mode === "category" && slug) q.set("category", slug);
      if (mode === "offers") q.set("sale", "true");
      if (search) q.set("search", search);
      if (selectedBrands.length) q.set("brand", selectedBrands.join(","));
      if (priceMin) q.set("min_price", priceMin);
      if (priceMax) q.set("max_price", priceMax);
      if (inStock) q.set("in_stock", "true");
      api.get(`/products?${q.toString()}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
    };
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [slug, search, mode, sort, selectedBrands, priceMin, priceMax, inStock]);

  useEffect(() => {
    if (mode === "category" && slug) api.get("/categories").then(({ data }) => setCat(data.find((c) => c.slug === slug)));
    else setCat(null);
  }, [slug, mode]);

  const title = mode === "offers" ? "🔥 Aktuelle tilbud" : mode === "category" ? `${cat?.icon_url ? "" : (cat?.icon || "")} ${cat?.name || "Kategori"}` : search ? `Søgning: "${search}"` : "Alle produkter";

  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <SlidePlacement where={mode === "category" ? "kategori" : "produktoversigt"} />
      <div className="mb-6">
        <p className="lux-eyebrow mb-1">Vores sortiment</p>
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="shop-title">{title}</h1>
        {!loading && <p className="mt-1 text-sm text-muted-foreground" data-testid="shop-count">Vi fandt {data.total} {data.total === 1 ? "produkt" : "produkter"}</p>}
      </div>

      <FilterBar
        sort={sort} setSort={setSort}
        brands={brands}
        selectedBrands={selectedBrands} setSelectedBrands={setSelectedBrands}
        priceMin={priceMin} setPriceMin={setPriceMin}
        priceMax={priceMax} setPriceMax={setPriceMax}
        inStock={inStock} setInStock={setInStock}
        onReset={resetFilters}
      />

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-card" />)}
        </div>
      ) : data.products.length === 0 ? (
        <div className="py-20 text-center">
          <p className="text-lg font-semibold">Vi kunne ikke finde det, du leder efter.</p>
          <p className="mt-1 text-sm text-muted-foreground">Prøv at justere dine filtre eller se alle vores produkter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
